import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  memo,
  useRef,
} from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { MapMarker } from "../map.types";
import MarkerWrapper from "./marker-wrapper";
import { createRegionClusters } from "./cluters.utils";

export type ClusteredMarkersProps = {
  markers: MapMarker[];
  onMarkerClick: (event: google.maps.MapMouseEvent, marker: MapMarker) => void;
  maxClusterZoom?: number;
};

const ClusteredMarkers = ({
  markers,
  onMarkerClick,
  maxClusterZoom = 13,
}: ClusteredMarkersProps) => {
  const map = useMap();
  const markersDataRef = useRef<{ [key: string]: google.maps.Marker }>({});
  const [markerRegistryVersion, setMarkerRegistryVersion] = useState(0);
  const markerRegistryUpdateScheduledRef = useRef(false);
  const isMountedRef = useRef(true);

  // Keep active clusterers and cleanly tear them down when needed.
  const clustersRef = useRef<MarkerClusterer[]>([]);
  const lastClusterBuildKeyRef = useRef<string>("");

  const cleanupClusters = useCallback(() => {
    clustersRef.current.forEach((cluster) => {
      cluster.clearMarkers(true);
      // Detach the overlay so its map idle listener and clusters are released.
      cluster.setMap(null);
    });
    clustersRef.current = [];
  }, []);

  /**
   * Rebuild clusters only when marker membership, grouping, or coordinates
   * change. Status/style updates should not trigger cluster teardown because
   * clearing clusterers briefly removes every marker from the map.
   */
  const clusterStructureKey = useMemo(
    () =>
      markers
        .map(
          (marker) =>
            `${marker.id}:${marker.region?.id || "unassigned"}:${marker.latitude ?? ""}:${marker.longitude ?? ""}`,
        )
        .sort()
        .join("|"),
    [markers],
  );

  const clusterMarkers = useMemo(() => markers, [clusterStructureKey]);
  const clusterBuildKey = useMemo(
    () => `${maxClusterZoom}|${clusterStructureKey}`,
    [maxClusterZoom, clusterStructureKey],
  );
  const markerIdSet = useMemo(
    () => new Set(clusterMarkers.map((marker) => marker.id)),
    [clusterMarkers],
  );

  const scheduleMarkerRegistryUpdate = useCallback(() => {
    if (markerRegistryUpdateScheduledRef.current) return;

    markerRegistryUpdateScheduledRef.current = true;
    const flushMarkerRegistryUpdate = () => {
      markerRegistryUpdateScheduledRef.current = false;
      if (!isMountedRef.current) return;

      setMarkerRegistryVersion((version) => version + 1);
    };

    if (typeof window.queueMicrotask === "function") {
      window.queueMicrotask(flushMarkerRegistryUpdate);
      return;
    }

    Promise.resolve().then(flushMarkerRegistryUpdate);
  }, []);

  const setMarkerRef = useCallback(
    (marker: google.maps.Marker | null, key: string) => {
      const existingMarker = markersDataRef.current[key];
      if ((marker && existingMarker) || (!marker && !existingMarker)) return;

      if (marker) {
        markersDataRef.current[key] = marker;
      } else {
        delete markersDataRef.current[key];
      }

      scheduleMarkerRegistryUpdate();
    },
    [scheduleMarkerRegistryUpdate],
  );

  useLayoutEffect(() => {
    if (!map) return;

    // Prune stale refs for markers that are no longer rendered.
    const markerRegistryKeys = Object.keys(markersDataRef.current);
    markerRegistryKeys.forEach((key) => {
      if (!markerIdSet.has(key)) {
        delete markersDataRef.current[key];
      }
    });

    const markersWithCoordinates = clusterMarkers.filter(
      ({ latitude, longitude }) =>
        latitude !== undefined && longitude !== undefined,
    );

    if (!markersWithCoordinates.length) {
      cleanupClusters();
      lastClusterBuildKeyRef.current = "";
      return;
    }

    // Wait until all currently rendered marker refs are available.
    const allMarkersReady = markersWithCoordinates.every(
      ({ id }) => !!markersDataRef.current[id],
    );
    if (!allMarkersReady) return;

    const shouldRebuildClusters =
      lastClusterBuildKeyRef.current !== clusterBuildKey ||
      clustersRef.current.length === 0;

    if (!shouldRebuildClusters) return;

    cleanupClusters();

    const nextClusters = createRegionClusters(
      clusterMarkers,
      markersDataRef.current,
      map,
      maxClusterZoom,
    );

    clustersRef.current = nextClusters;
    lastClusterBuildKeyRef.current = clusterBuildKey;
  }, [
    map,
    markerRegistryVersion,
    maxClusterZoom,
    clusterStructureKey,
    clusterBuildKey,
    clusterMarkers,
    markerIdSet,
    cleanupClusters,
  ]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      cleanupClusters();
    };
  }, [cleanupClusters]);

  return (
    <>
      {markers.map((marker) => (
        <MarkerWrapper
          key={marker.id}
          locationMarker={marker}
          setMarkerRef={setMarkerRef}
          onMarkerClick={onMarkerClick}
        />
      ))}
    </>
  );
};

export default memo(ClusteredMarkers);
